import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useState } from "react";

import type { DashboardNotificationSeverity } from "@situate/contracts/runtime";
import type {
  DashboardAlertPreviewItem,
  DashboardSourceState,
} from "@/features/deployment-dashboard/overview/model/dashboard-monitoring-data";
import { DashboardWidgetAccessState } from "@/features/deployment-dashboard/widgets/dashboard-widget-access-state";
import { DashboardWidgetContentState } from "@/features/deployment-dashboard/widgets/dashboard-widget-content-state";
import { getDefaultNotificationPreviewCount } from "@/features/deployment-dashboard/widgets/panels/active-notifications-preview/active-notifications-preview-panel.utils";
import { ActiveNotificationCard } from "@/features/deployment-dashboard/widgets/panels/active-notifications-preview/active-notification-card";
import { ConsoleSurface, SectionHeading } from "@/shared/ui";
import { NotificationsIcon } from "@/shared/ui/console-icons";

interface ActiveNotificationsPreviewPanelProps {
  alerts: DashboardSourceState<DashboardAlertPreviewItem[]>;
  onSelectAlert: (alertId: string) => void;
}

export function ActiveNotificationsPreviewPanel({
  alerts,
  onSelectAlert,
}: ActiveNotificationsPreviewPanelProps) {
  const [page, setPage] = useState(0);
  const items = alerts.data ?? [];
  const severityRank: Record<DashboardNotificationSeverity, number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
    info: 4,
  };
  const activeAlerts = items
    .filter((alert) => alert.status !== "resolved")
    .sort(
      (left, right) =>
        (left.status === "active" ? 0 : 1) - (right.status === "active" ? 0 : 1) ||
        severityRank[left.severity] - severityRank[right.severity]
    );
  const pageSize = getDefaultNotificationPreviewCount();
  const pageCount = Math.ceil(activeAlerts.length / pageSize);
  const currentPage = Math.min(page, Math.max(pageCount - 1, 0));
  const previewAlerts = activeAlerts.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  return (
    <ConsoleSurface
      component="section"
      sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 242 }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
        })}
      >
        <SectionHeading
          icon={<NotificationsIcon sx={{ fontSize: "0.875rem" }} />}
          component="h2"
          action={
            activeAlerts.length > 0 ? (
              <Chip
                aria-label={`${activeAlerts.length} unresolved alerts`}
                label={activeAlerts.length}
                size="small"
                color="error"
                sx={{ minWidth: 24, height: 22, fontWeight: 700 }}
              />
            ) : undefined
          }
        >
          Active Alerts
        </SectionHeading>
      </Box>
      <Box sx={{ flex: 1, px: previewAlerts.length === 0 ? 2.5 : 0 }}>
        {previewAlerts.length === 0 ? (
          alerts.status === "loading" || alerts.status === "empty" ? (
            <DashboardWidgetContentState state={alerts.status} minHeight={140} />
          ) : (
            <DashboardWidgetAccessState
              status={alerts.status === "ready" ? "empty" : alerts.status}
            />
          )
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 2.5, py: 1.5 }}>
              {previewAlerts.map((alert) => (
                <ActiveNotificationCard
                  key={alert.id}
                  notification={alert}
                  onSelect={() => onSelectAlert(alert.id)}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
      {alerts.data != null && (
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            px: 2.5,
            py: 1.25,
            borderTop: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
          })}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: 1, textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            Showing {activeAlerts.length === 0 ? 0 : currentPage + 1} of {activeAlerts.length}{" "}
            alerts
          </Typography>
          {pageCount > 1 && (
            <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
              <IconButton
                aria-label="Show previous alerts"
                size="small"
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 0}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label="Show next alerts"
                size="small"
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === pageCount - 1}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </ConsoleSurface>
  );
}
